/**
 * `<store-locator>` (sections/store-locator.liquid): enhances the server-rendered list of
 * locations, which works without JS (CSS filter chips, one <details> per location).
 *
 * - Search: name, city, postcode, country, street, type and "Other names", without accents
 *   (München = Munchen, Straße = Strasse). The count is announced.
 * - Use My Location: asks the browser once, sorts by distance and shows it; the position is only
 *   used in the browser.
 * - Opening state: computed from the hours in the location's own time zone, every minute. A
 *   nationwide public holiday in the location's country (assets/public-holidays.json, built by
 *   .src/tools/build-public-holidays.mjs) shows as one instead of open or closed, and counts as
 *   a closed day when the next opening is looked for. A location without hours has no state.
 * - Times and distances follow the shopper's language and country: twelve hours where that
 *   locale's clock has them ("6 PM", "10 AM–6 PM"; the server renders 24 hours), miles in the
 *   countries that use them.
 * - Map: a world map in the canvas's look (assets/world-map-110m.svg, and world-map-50m.svg once
 *   zoomed in; .src/tools/build-world-map.py builds both from Natural Earth). Pins sit at the
 *   location's coordinates in the same Equal Earth projection, merge into numbered clusters when
 *   closer than 16px, and keep their size at any zoom. Zoom buttons, "Show all locations",
 *   dragging and pinching move the view; the map only changes its viewBox and the pins move by
 *   transform, so nothing shifts the layout.
 * - Picking a pin opens the location (wide screens) or its card over the map (phones); opening a
 *   location in the list centers its pin.
 */

// The world drawing's box in map units (the SVGs' viewBox), 1000 per Earth radius.
const WORLD = { x: -2750, y: -1350, width: 5500, height: 2700 };
const ZOOM_MAX = 24;
const FIT_CAP = 14;
const PICK_ZOOM = 12;
const DETAIL_FROM = 1;
const COUNTRIES_BELOW = 9;
const CITIES_FROM = 8;
const LABEL_AREA_PX = 2500;
const CLUSTER_PX = 16;
const DRAG_PX = 4;

// Equal Earth (Šavrič, Patterson, Jenny 2018) around Greenwich, north up. Same constants as
// .src/tools/build-world-map.py.
const RAD = Math.PI / 180;
const A1 = 1.340264;
const A2 = -0.081106;
const A3 = 0.000893;
const A4 = 0.003796;
const M = Math.sqrt(3) / 2;

function project(lat, lon) {
  const theta = Math.asin(M * Math.sin(lat * RAD));
  const t2 = theta * theta;
  const t6 = t2 * t2 * t2;
  const x = (lon * RAD * Math.cos(theta)) / (M * (A1 + 3 * A2 * t2 + t6 * (7 * A3 + 9 * A4 * t2)));
  const y = theta * (A1 + A2 * t2 + t6 * (A3 + A4 * t2));
  return { x: x * 1000, y: -y * 1000 };
}

// Country labels: ISO code, position and area in map units (centroid of the largest part), from
// build-world-map.py. Names follow the page's language; a label shows once the country is large
// enough on screen.
const COUNTRIES = [
  ["RU",1097,-1084,415049], ["CA",-1167,-1039,242839], ["US",-1323,-757,232496],
  ["CN",1407,-700,230869], ["BR",-790,213,209388], ["AU",1921,502,189834], ["IN",1149,-452,77752],
  ["AR",-889,673,68544], ["KZ",847,-908,66816], ["CD",355,58,57525], ["DZ",37,-554,57223],
  ["GL",-414,-1228,53017], ["MX",-1473,-474,48345], ["SA",643,-479,47456], ["ID",1714,4,46049],
  ["SD",441,-320,45918], ["LY",257,-535,40054], ["IR",756,-638,39990], ["MN",1311,-889,38444],
  ["PE",-1111,184,31924], ["TD",275,-307,31304], ["ML",-53,-347,30981], ["ZA",356,572,30843],
  ["AO",261,248,30773], ["NE",137,-349,29191], ["CO",-1097,-79,28084], ["ET",592,-174,27879],
  ["BO",-953,335,26862], ["MR",-151,-404,25599], ["EG",426,-525,24735], ["TZ",521,127,23306],
  ["VE",-991,-144,22521], ["NG",121,-194,22445], ["PK",973,-589,21531], ["NA",249,441,20287],
  ["MZ",523,345,19476], ["TR",475,-756,19200], ["ZM",412,271,18626], ["CL",-971,667,18179],
  ["MM",1403,-423,16316], ["AF",910,-663,15815], ["FR",31,-885,15642], ["SS",453,-148,15377],
  ["CF",307,-133,15295], ["UA",393,-924,14703], ["MG",683,387,14647], ["KE",568,-12,14643],
  ["MA",-122,-587,14351], ["BW",345,443,14309], ["TH",1493,-303,12702], ["ES",-47,-780,12469],
  ["SO",685,-96,11672], ["TM",796,-758,11591], ["CM",191,-115,11488], ["PG",2161,133,11434],
  ["YE",701,-320,11179], ["UZ",834,-804,11029], ["SE",181,-1118,10920], ["IQ",607,-648,10762],
  ["PY",-844,463,9877], ["ZW",437,381,9618], ["NO",139,-1133,9410], ["JP",1875,-714,9121],
  ["DE",127,-957,8768], ["CG",229,17,8542], ["FI",285,-1142,8140], ["VN",1565,-332,8106],
  ["MY",1723,-73,8066], ["CI",-83,-154,7939], ["PL",237,-974,7700], ["OM",817,-411,7694],
  ["IT",160,-834,7388], ["PH",1792,-321,7143], ["BF",-26,-247,6743], ["NZ",2215,842,6572],
  ["GA",177,12,6413], ["EC",-1178,29,6300], ["GN",-163,-211,6035], ["UG",487,-26,5977],
  ["GH",-18,-161,5906], ["GB",-29,-1000,5888], ["RO",320,-874,5806], ["LA",1521,-371,5642],
  ["GY",-885,-97,5211], ["BY",337,-995,5093], ["KG",984,-800,4912], ["SN",-214,-289,4861],
  ["SY",528,-685,4579], ["KH",1559,-257,4469], ["UY",-778,645,4381], ["TN",132,-669,3860],
  ["NP",1190,-560,3623], ["SR",-840,-84,3587], ["TJ",955,-748,3500], ["BD",1301,-476,3336],
  ["GR",302,-765,3193], ["NI",-1263,-259,3190], ["ER",574,-309,3048], ["KP",1693,-777,3018],
  ["MW",509,266,2946], ["BJ",35,-194,2880], ["HN",-1282,-298,2795], ["BG",330,-822,2767],
  ["CU",-1147,-432,2695], ["GT",-1334,-316,2674], ["IS",-200,-1152,2487], ["KR",1740,-711,2388],
  ["LR",-140,-131,2362], ["HU",246,-896,2291], ["EH",-177,-482,2240], ["PT",-107,-768,2233],
  ["JO",514,-616,2179], ["AZ",634,-780,2131], ["AT",178,-903,2070], ["CZ",190,-937,1937],
  ["RS",270,-847,1912], ["PA",-1198,-172,1825], ["SL",-176,-173,1774], ["AE",783,-477,1753],
  ["GE",572,-812,1704], ["IE",-98,-990,1689], ["LK",1208,-154,1626], ["LT",283,-1022,1587],
  ["LV",291,-1045,1585], ["TG",14,-172,1403], ["HR",211,-863,1339], ["CR",-1257,-202,1270],
  ["BA",230,-846,1270], ["DO",-1033,-379,1197], ["SK",244,-921,1188], ["EE",296,-1070,1112],
  ["DK",110,-1036,1043], ["CH",104,-890,1014], ["BT",1286,-544,982], ["NL",69,-977,909],
  ["TW",1745,-474,880], ["MD",361,-896,820], ["GW",-222,-243,787], ["BE",57,-952,755],
  ["LS",398,585,734], ["AM",598,-780,730], ["AL",265,-794,694], ["BI",449,68,674],
  ["HT",-1064,-380,657], ["GQ",157,-32,653], ["MK",286,-802,626], ["RW",450,40,624],
  ["SB",2392,194,620], ["BZ",-1305,-346,559], ["IL",489,-620,551], ["DJ",634,-237,534],
  ["SV",-1318,-277,500], ["SI",189,-879,495], ["NC",2406,427,457], ["FJ",2615,358,447],
  ["KW",671,-580,426], ["SZ",449,528,419], ["TL",1883,178,370], ["ME",252,-823,338],
  ["BS",-1122,-492,309], ["FK",-718,969,277], ["JM",-1135,-365,272], ["VU",2467,306,271],
  ["QA",734,-504,270], ["GM",-228,-271,252], ["LB",495,-666,245], ["PR",-976,-366,221],
  ["TF",867,931,169], ["PS",491,-629,158], ["CY",453,-684,140], ["BN",1721,-91,138],
  ["TT",-914,-210,127], ["GS",-438,1008,91], ["CV",-350,-304,88], ["WS",-2558,275,64],
  ["LU",76,-938,64], ["PF",-2196,355,58], ["MU",840,406,45], ["KM",645,235,39],
  ["HK",1654,-448,25], ["FO",-76,-1117,24], ["ST",99,-5,23], ["KI",-2366,-37,19],
  ["DM",-907,-311,18], ["AX",225,-1091,17], ["IM",-54,-1006,14], ["BH",723,-518,13],
  ["FM",2371,-139,12], ["SG",1561,-28,12], ["LC",-904,-280,12], ["GU",2148,-271,11],
  ["TO",-2549,424,10], ["CW",-1026,-246,10], ["BB",-884,-266,10], ["HM",889,989,10],
  ["AG",-910,-343,10], ["AD",21,-819,10], ["MP",2155,-306,8], ["VI",-952,-356,8],
  ["TC",-1042,-436,8], ["KY",-1189,-388,7], ["PW",2015,-152,7], ["GD",-918,-244,7],
  ["MH",2545,-120,6], ["VC",-909,-267,6], ["MT",197,-701,6], ["KN",-923,-348,5], ["SH",-84,321,4],
  ["SC",833,94,4], ["NU",-2487,382,4], ["PM",-716,-892,4], ["AS",-2529,288,3], ["IO",1085,148,3],
  ["JE",-27,-929,3], ["AW",-1040,-252,3], ["LI",121,-896,3], ["AI",-925,-366,2],
  ["BM",-901,-636,2], ["VG",-944,-371,2], ["MS",-916,-336,2], ["SM",162,-842,2],
  ["WF",-2639,288,2], ["GG",-32,-933,1], ["CK",-2324,425,1], ["MV",1104,-85,1], ["MF",-926,-363,1],
  ["MO",1646,-444,1], ["NF",2372,575,1], ["SX",-926,-363,1]
];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// The countries whose shoppers read distances in miles.
const MILES = new Set(["US", "GB", "LR", "MM"]);
const KM_PER_MILE = 1.609344;

const norm = (text) =>
  String(text).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ß/g, "ss");

// Strings come from the locale with [placeholders] ({{ }} would end the Liquid tag).
const fill = (template, values) =>
  Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, value), template);

const minutes = (time) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

// "10:00-13:00, 14:00-18:00" → [[600, 780, "10:00", "13:00"], [840, 1080, "14:00", "18:00"]].
// A close before the open ("20:00-02:00") runs into the next day.
const ranges = (hours) =>
  (hours || "")
    .split(",")
    .map((part) => part.trim().split("-"))
    .filter((pair) => pair.length === 2)
    .map(([open, close]) => {
      const from = minutes(open);
      let to = minutes(close);
      if (to <= from) to += 1440;
      return [from, to, open, close];
    });

function distanceKm(a, b) {
  const dLat = (b.lat - a.lat) * RAD;
  const dLon = (b.lon - a.lon) * RAD;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

class StoreLocator extends HTMLElement {
  #abort = null;
  #timer = 0;
  #frame = 0;
  #resize = null;
  #wide = window.matchMedia("(min-width: 64rem)");
  #items = [];
  #query = "";
  #selected = null;
  #me = null;
  #locating = false;
  #view = null;
  #shown = null;
  #fromCard = false;
  #pointers = new Map();
  #drag = null;
  #suppressClick = false;
  #pins = [];
  #labels = new Map();
  #maps = {};

  connectedCallback() {
    this.strings = JSON.parse(this.querySelector("[data-strings]")?.textContent || "{}");
    const locale = this.dataset.locale || document.documentElement.lang || "en";
    this.plural = new Intl.PluralRules(locale);
    this.number = (value, digits) =>
      new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(value);
    // The clock and the unit follow language plus country ("en-US"): the language alone would
    // give every English page twelve hours.
    const country = (this.dataset.country || "").toUpperCase();
    const tag = country ? `${locale.split("-")[0]}-${country}` : locale;
    let cycle = "h23";
    try {
      cycle = new Intl.DateTimeFormat(tag, { hour: "numeric" }).resolvedOptions().hourCycle || cycle;
    } catch {
      // An unknown tag keeps the 24 hours the server rendered.
    }
    this.twelve = cycle === "h12" || cycle === "h11";
    this.miles = MILES.has(country);
    this.weekday = new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" });
    this.holidays = {};
    try {
      this.regions = new Intl.DisplayNames([locale], { type: "region" });
    } catch {
      this.regions = null;
    }

    this.list = this.querySelector("[data-list]");
    this.scroller = this.querySelector("[data-scroll]");
    this.input = this.querySelector("[data-query]");
    this.map = this.querySelector("[data-map]");
    this.viewport = this.querySelector("[data-map-viewport]");
    this.world = this.querySelector("[data-map-world]");
    this.pinLayer = this.querySelector("[data-map-pins]");
    this.labelLayer = this.querySelector("[data-map-labels]");
    this.card = this.querySelector("[data-map-card]");

    this.#items = [...this.querySelectorAll(".loc")].map((li, index) => {
      const lat = Number(li.dataset.lat);
      const lon = Number(li.dataset.lon);
      let hours;
      try {
        hours = JSON.parse(li.dataset.hours || "[]");
      } catch {
        hours = [];
      }
      return {
        li,
        index,
        id: li.dataset.id,
        details: li.querySelector("details"),
        name: li.querySelector(".loc__name")?.textContent.trim() || "",
        city: li.dataset.city || "",
        group: li.dataset.group,
        search: norm(li.dataset.search || ""),
        lat,
        lon,
        ...project(lat, lon),
        hours: hours.map(ranges),
        country: (li.dataset.country || "").toUpperCase(),
        zone: this.#zone(li.dataset.tz),
        visible: true,
        distance: null,
      };
    });

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // The list follows every letter, so an on-screen keyboard's search key has nothing left to do
    // but go: on a touch screen it puts the keyboard away. Not the Return that ends an input
    // method's composition (Safari reports that one as Enter, with the key code 229).
    this.input?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" || event.isComposing || event.keyCode === 229) return;
      if (window.matchMedia("(pointer: coarse)").matches) this.input.blur();
    }, { signal });
    this.input?.addEventListener("input", () => {
      this.#query = this.input.value;
      this.#closeOpen();
      this.#view = null;
      this.#apply();
    }, { signal });
    this.querySelectorAll("[data-clear]").forEach((button) =>
      button.addEventListener("click", () => this.#clearSearch(), { signal }));
    this.querySelectorAll("[data-kind]").forEach((radio) =>
      radio.addEventListener("change", () => {
        this.#closeOpen();
        this.#view = null;
        this.#apply();
      }, { signal }));
    this.querySelectorAll("[data-view]").forEach((radio) =>
      radio.addEventListener("change", () => {
        this.#hideCard();
        this.#render();
      }, { signal }));
    this.querySelector("[data-locate]")?.addEventListener("click", () => this.#locate(), { signal });
    this.list?.addEventListener("toggle", (event) => this.#onToggle(event), { capture: true, signal });

    this.querySelectorAll("[data-zoom]").forEach((button) =>
      button.addEventListener("click", () => this.#zoom(button.dataset.zoom), { signal }));
    this.pinLayer?.addEventListener("click", (event) => this.#onPin(event), { signal });
    this.viewport?.addEventListener("pointerdown", (event) => this.#onDown(event), { signal });
    this.viewport?.addEventListener("pointermove", (event) => this.#onMove(event), { signal });
    ["pointerup", "pointercancel"].forEach((type) =>
      this.viewport?.addEventListener(type, (event) => this.#onUp(event), { signal }));
    this.viewport?.addEventListener("click", (event) => {
      if (this.#suppressClick) event.stopPropagation();
    }, { capture: true, signal });

    this.querySelector("[data-card-close]")?.addEventListener("click", () => {
      this.#selected = null;
      this.#view = null;
      this.#hideCard();
      this.#render();
    }, { signal });
    this.querySelector("[data-card-details]")?.addEventListener("click", () => {
      const item = this.#find(this.#selected);
      if (!item) return;
      this.#fromCard = true;
      item.details.open = true;
      item.details.querySelector("summary")?.focus({ preventScroll: true });
    }, { signal });

    // Theme editor: selecting the section shows one open location.
    document.addEventListener("shopify:section:select", (event) => {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      const first = this.#items.find((item) => item.visible);
      if (first) first.details.open = true;
    }, { signal });
    document.addEventListener("shopify:section:deselect", (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.#closeOpen();
    }, { signal });

    this.#resize = new ResizeObserver(() => this.#render());
    if (this.viewport) this.#resize.observe(this.viewport);

    if (this.twelve) {
      this.querySelectorAll("[data-hours-value]").forEach((el) => {
        el.textContent = el.dataset.hoursValue
          .split(",")
          .map((part) => part.trim().split("-").map((time) => this.#time(time.trim())).join("–"))
          .join(", ");
      });
    }

    // The states wait for the holidays (one small file of the theme), so no location reads
    // "Open now" for a moment on a holiday; without the file they show without holidays.
    this.#loadHolidays(signal).then(() => {
      if (signal.aborted) return;
      this.#updateStatus();
      this.#timer = setInterval(() => this.#updateStatus(), 60000);
    });
    this.#apply();
  }

  async #loadHolidays(signal) {
    const url = this.dataset.holidays;
    if (!url) return;
    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return;
      const { days } = await response.json();
      this.holidays = Object.fromEntries(Object.entries(days || {}).map(([code, dates]) => [code, new Set(dates)]));
    } catch {
      // No file, no holidays.
    }
  }

  /* "18:00" as the shopper's clock writes it: unchanged, or "6 PM" and "5:30 PM". */
  #time(time) {
    if (!this.twelve) return time;
    const [hour, minute] = time.split(":").map(Number);
    const half = hour % 24 < 12 ? "AM" : "PM";
    return `${hour % 12 || 12}${minute ? `:${String(minute).padStart(2, "0")}` : ""}\u00a0${half}`;
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resize?.disconnect();
    clearInterval(this.#timer);
    cancelAnimationFrame(this.#frame);
  }

  /* The two map drawings, fetched once and placed inline so every zoom level stays sharp. */
  async #loadMap(kind) {
    if (!this.world || this.#maps[kind]) return;
    const url = this.world.dataset[kind === "world" ? "world" : "detail"];
    this.#maps[kind] = "loading";
    try {
      const text = await fetch(url).then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      });
      const svg = document.importNode(new DOMParser().parseFromString(text, "image/svg+xml").documentElement, true);
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("focusable", "false");
      svg.classList.add("map__drawing");
      // SVG elements have no hidden property, only the attribute.
      svg.setAttribute("hidden", "");
      this.world.append(svg);
      this.#maps[kind] = svg;
      this.#render();
    } catch {
      // Once is enough: the map stays without this drawing instead of asking on every frame.
      this.#maps[kind] = "failed";
    }
  }

  #zone(zone) {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: zone || "Europe/Berlin" });
      return zone || "Europe/Berlin";
    } catch {
      return "Europe/Berlin";
    }
  }

  #find(id) {
    return this.#items.find((item) => item.id === id);
  }


  /* Search, filter, order, count, empty state. */
  #apply() {
    const query = norm(this.#query.trim());
    const kind = this.querySelector("[data-kind]:checked")?.value || "all";
    this.#items.forEach((item) => {
      item.visible = (kind === "all" || item.group === kind) && (!query || item.search.includes(query));
      item.li.hidden = !item.visible;
      item.distance = this.#me ? distanceKm(this.#me, item) : null;
      this.#showDistance(item);
    });

    const ordered = [...this.#items].sort((a, b) =>
      this.#me ? a.distance - b.distance : a.index - b.index);
    const current = [...this.list.children];
    if (ordered.some((item, i) => current[i] !== item.li)) {
      ordered.forEach((item) => this.list.append(item.li));
    }

    const visible = ordered.filter((item) => item.visible);
    const count = this.querySelector("[data-count]");
    const template = this.plural.select(visible.length) === "one" ? this.strings.countOne : this.strings.count;
    const countText = fill(template || "[count]", { count: visible.length });
    if (count && count.textContent !== countText) count.textContent = countText;

    const empty = this.querySelector("[data-empty]");
    if (empty) {
      empty.hidden = visible.length > 0;
      const title = empty.querySelector("[data-empty-title]");
      if (title) title.textContent = fill(this.strings.noMatch || "", { query: this.#query.trim() });
    }
    this.querySelectorAll("[data-clear]").forEach((button) => {
      if (button.closest("search")) button.hidden = this.#query === "";
    });

    if (this.#selected && !this.#find(this.#selected)?.visible) {
      this.#selected = null;
      this.#hideCard();
    }
    this.#render();
  }

  #clearSearch() {
    this.#query = "";
    if (this.input) this.input.value = "";
    this.#view = null;
    this.#apply();
    this.input?.focus();
  }

  #closeOpen() {
    this.#items.forEach((item) => {
      if (item.details.open) item.details.open = false;
    });
  }

  #showDistance(item) {
    const row = item.li.querySelector("[data-distance]");
    const away = item.li.querySelector("[data-distance-away]");
    let text = "";
    if (item.distance !== null) {
      const value = this.miles ? item.distance / KM_PER_MILE : item.distance;
      text = `${this.number(value, value < 10 ? 1 : 0)} ${this.miles ? "mi" : "km"}`;
    }
    if (row) row.textContent = text;
    if (away) away.textContent = text ? fill(this.strings.away || "[distance]", { distance: text }) : "";
  }

  /* Opening state in each location's time zone. */
  #now(zone) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      weekday: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type)?.value;
    return {
      day: WEEKDAYS.indexOf(get("weekday")),
      minute: Number(get("hour")) * 60 + Number(get("minute")),
      date: [Number(get("year")), Number(get("month")), Number(get("day"))],
    };
  }

  /* Whether the location's calendar day, `offset` days from its today, is a public holiday in
     its country. Calendar days are counted in UTC, so a clock change cannot skip one. */
  #holiday(item, [year, month, day], offset) {
    const date = new Date(Date.UTC(year, month - 1, day + offset)).toISOString().slice(0, 10);
    return this.holidays[item.country]?.has(date) ?? false;
  }

  /* null for a location without hours: it has no state to show, not "Closed". */
  #status(item) {
    if (!item.hours.some((windows) => windows.length > 0)) return null;
    const { day, minute, date } = this.#now(item.zone);
    if (this.#holiday(item, date, 0)) return { open: false, text: this.strings.holiday };
    const today = item.hours[day] || [];
    const yesterday = this.#holiday(item, date, -1) ? [] : item.hours[(day + 6) % 7] || [];
    const open =
      today.find(([from, to]) => minute >= from && minute < to) ||
      yesterday.find(([, to]) => to > 1440 && minute + 1440 < to);
    const later = today.find(([from]) => minute < from);
    if (open) {
      const time = this.#time(open[3]);
      // Another window follows today after a break (not one that starts as this one ends).
      if (later && later[0] > open[1]) return { open: true, text: fill(this.strings.openReopens, { time, reopen: this.#time(later[2]) }) };
      return { open: true, text: fill(this.strings.openUntil, { time }) };
    }
    if (later) {
      // "Reopens" only in a break, once a window of today is over; before the day's first
      // opening it "opens today".
      const template = today.some(([, to]) => to <= minute) ? this.strings.reopens : this.strings.opensToday;
      return { open: false, text: fill(template, { time: this.#time(later[2]) }) };
    }
    // The next day with hours that is no holiday; two weeks reach past any run of holidays.
    for (let i = 1; i <= 14; i += 1) {
      const next = (day + i) % 7;
      const first = (item.hours[next] || [])[0];
      if (first && !this.#holiday(item, date, i)) {
        const template = i === 1 ? this.strings.opensTomorrow : this.strings.opensOn;
        // 1970-01-04 was a Sunday.
        const name = this.weekday.format(new Date(Date.UTC(1970, 0, 4 + next)));
        return { open: false, text: fill(template, { time: this.#time(first[2]), day: name }) };
      }
    }
    return { open: false, text: this.strings.closed };
  }

  #updateStatus() {
    this.#items.forEach((item) => {
      const state = this.#status(item) || { open: false, text: "" };
      item.li.querySelectorAll("[data-status]").forEach((el) => {
        el.textContent = state.text;
        el.dataset.status = state.open ? "open" : "off";
      });
      const { day } = this.#now(item.zone);
      item.li.querySelectorAll("[data-days]").forEach((row) => {
        row.toggleAttribute("data-today", row.dataset.days.split(" ").includes(String(day)));
      });
      item.statusText = state.text;
      item.open = state.open;
    });
    if (this.#selected) this.#fillCard(this.#find(this.#selected));
  }

  /* Use My Location. */
  #locate() {
    const button = this.querySelector("[data-locate]");
    const label = this.querySelector("[data-locate-label]");
    const sort = this.querySelector("[data-sort]");
    if (this.#me) {
      this.#me = null;
      button?.setAttribute("aria-pressed", "false");
      if (label) label.textContent = this.strings.locate;
      if (sort) sort.textContent = "";
      this.#view = null;
      this.#apply();
      return;
    }
    if (this.#locating) return;
    if (!navigator.geolocation) {
      if (sort) sort.textContent = this.strings.locateFailed;
      return;
    }
    this.#locating = true;
    button?.setAttribute("aria-busy", "true");
    if (sort) sort.textContent = this.strings.locating;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.#locating = false;
        const { latitude: lat, longitude: lon } = position.coords;
        this.#me = { lat, lon, ...project(lat, lon) };
        button?.removeAttribute("aria-busy");
        button?.setAttribute("aria-pressed", "true");
        if (label) label.textContent = this.strings.clearLocation;
        if (sort) sort.textContent = this.strings.nearest;
        this.#closeOpen();
        this.#selected = null;
        this.#hideCard();
        this.#view = null;
        this.#apply();
        if (this.scroller) this.scroller.scrollTop = 0;
      },
      () => {
        this.#locating = false;
        button?.removeAttribute("aria-busy");
        if (sort) sort.textContent = this.strings.locateFailed;
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  }

  /* A location opened or closed in the list. */
  #onToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    const item = this.#items.find((entry) => entry.details === details);
    if (!item) return;
    if (details.open) {
      this.#selected = item.id;
      this.#view = null;
      if (this.scroller) this.scroller.scrollTop = 0;
      if (this.#wide.matches || !this.#fromCard) this.#hideCard();
    } else if (this.#selected === item.id && !this.#items.some((entry) => entry.details.open)) {
      if (this.#fromCard && !this.#wide.matches) {
        this.#showCard(item);
      } else {
        this.#selected = null;
      }
      this.#fromCard = false;
      this.#view = null;
      requestAnimationFrame(() => {
        if (this.scroller && !item.li.hidden) {
          this.scroller.scrollTop = Math.max(0, item.li.offsetTop - this.scroller.clientHeight / 3);
        }
      });
    }
    this.#render();
  }

  // The pin a click means. Every dot sits in a 44px target, and the targets of dots closer than
  // that overlap, so the topmost target is not always the dot under the pointer: of the pins
  // whose target holds the point, the one whose drawn dot is nearest answers (measured to the
  // dot's edge, so a larger dot, a cluster or the picked location, keeps what it covers). On a
  // tie the topmost one stays, and so does a click without a position (keyboard).
  #pinAt(event) {
    const target = event.target.closest(".map__pin");
    if (!target || (!event.clientX && !event.clientY)) return target;
    const reach = (pin) => {
      const box = pin.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) return Infinity;
      const dot = parseFloat(getComputedStyle(pin, "::before").width) / 2 || 0;
      return Math.hypot(event.clientX - (box.left + box.width / 2), event.clientY - (box.top + box.height / 2)) - dot;
    };
    let nearest = target;
    let distance = reach(target);
    for (const pin of this.#pins) {
      if (pin.hidden || pin === target) continue;
      const d = reach(pin);
      if (d < distance) {
        nearest = pin;
        distance = d;
      }
    }
    return nearest;
  }

  #onPin(event) {
    const pin = this.#pinAt(event);
    if (!pin || this.#suppressClick) return;
    if (pin.dataset.members) {
      const members = pin.dataset.members.split(",").map((id) => this.#find(id)).filter(Boolean);
      this.#view = this.#spread(members);
      this.#render();
      return;
    }
    const item = this.#find(pin.dataset.id);
    if (!item) return;
    if (this.#wide.matches) {
      item.details.open = true;
      item.details.querySelector("summary")?.focus({ preventScroll: true });
    } else {
      this.#selected = item.id;
      this.#view = null;
      this.#showCard(item);
      this.#render();
    }
  }

  #fillCard(item) {
    if (!item || !this.card) return;
    const set = (selector, text) => {
      const el = this.card.querySelector(selector);
      if (el) el.textContent = text;
    };
    set("[data-card-type]", item.li.querySelector(".loc__type")?.textContent.trim() || "");
    set("[data-card-name]", item.name);
    set("[data-card-address]", item.li.querySelector(".loc__address")?.textContent.trim() || "");
    const status = this.card.querySelector("[data-card-status]");
    if (status) {
      status.textContent = item.statusText || "";
      status.dataset.status = item.open ? "open" : "off";
      status.hidden = !item.statusText;
    }
    const directions = this.card.querySelector("[data-card-directions]");
    const link = item.li.querySelector("[data-directions]");
    if (directions && link) directions.href = link.href;
  }

  #showCard(item) {
    if (!this.card) return;
    this.#fillCard(item);
    this.card.hidden = false;
  }

  #hideCard() {
    if (this.card) this.card.hidden = true;
  }

  /* The map view: { s: scale, cx, cy: center in map units }. */
  #size() {
    return { width: this.viewport?.clientWidth || 0, height: this.viewport?.clientHeight || 0 };
  }

  // Zoomed all the way out, the whole world fits.
  #minZoom() {
    const { width, height } = this.#size();
    return width && height ? Math.min(width / WORLD.width, height / WORLD.height) : 0.1;
  }

  #clampZoom(s) {
    return Math.max(this.#minZoom(), Math.min(ZOOM_MAX, s));
  }

  #fit(points, cap = FIT_CAP) {
    const { width, height } = this.#size();
    if (!points.length || !width || !height) {
      return { s: this.#minZoom(), cx: WORLD.x + WORLD.width / 2, cy: WORLD.y + WORLD.height / 2 };
    }
    const pad = width < 600 ? 48 : 104;
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const x1 = Math.min(...xs);
    const x2 = Math.max(...xs);
    const y1 = Math.min(...ys);
    const y2 = Math.max(...ys);
    const fitWidth = x2 > x1 ? (width - pad * 2) / (x2 - x1) : Infinity;
    const fitHeight = y2 > y1 ? (height - pad * 2) / (y2 - y1) : Infinity;
    const s = this.#clampZoom(Math.min(cap, fitWidth, fitHeight));
    return { s, cx: (x1 + x2) / 2, cy: (y1 + y2) / 2 };
  }

  // A cluster's view: all its members in sight and zoomed far enough that they come apart.
  #spread(members) {
    const view = this.#fit(members, ZOOM_MAX);
    let closest = Infinity;
    members.forEach((a, i) => members.slice(i + 1).forEach((b) => {
      closest = Math.min(closest, Math.hypot(a.x - b.x, a.y - b.y));
    }));
    const needed = closest > 0 ? (CLUSTER_PX * 1.5) / closest : ZOOM_MAX;
    const current = this.#shown?.s ?? view.s;
    return { ...view, s: Math.min(ZOOM_MAX, Math.max(view.s, needed, current * 1.5)) };
  }

  #autoView() {
    const visible = this.#items.filter((item) => item.visible);
    const selected = this.#find(this.#selected);
    if (selected) {
      const home = this.#fit(this.#items);
      const s = Math.min(ZOOM_MAX, Math.max(home.s * 2, PICK_ZOOM));
      const cardOpen = this.card && !this.card.hidden;
      return { s, cx: selected.x, cy: cardOpen ? selected.y + 90 / s : selected.y };
    }
    if (this.#me) {
      const nearest = visible.slice().sort((a, b) => a.distance - b.distance).slice(0, 3);
      return this.#fit([this.#me, ...nearest]);
    }
    return this.#fit(visible.length ? visible : this.#items);
  }

  #zoom(how) {
    const view = this.#shown || this.#autoView();
    if (how === "fit") {
      this.#view = this.#fit(this.#items);
    } else {
      const factor = how === "in" ? 1.5 : 1 / 1.5;
      this.#view = { ...view, s: this.#clampZoom(view.s * factor) };
    }
    this.#render();
  }

  #render() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#draw());
  }

  #draw() {
    const { width, height } = this.#size();
    if (!width || !height || !this.world) return;
    const wanted = this.#view || this.#autoView();
    // Never further out than the whole world, never past its edges; a view larger than the
    // world keeps it centered.
    const s = this.#clampZoom(wanted.s);
    const inside = (center, span, start, size) =>
      span >= size ? start + size / 2 : Math.max(start + span / 2, Math.min(start + size - span / 2, center));
    const cx = inside(wanted.cx, width / s, WORLD.x, WORLD.width);
    const cy = inside(wanted.cy, height / s, WORLD.y, WORLD.height);
    const view = { s, cx, cy };
    if (this.#view) this.#view = view;
    this.#shown = view;
    // Past the view of all locations a finger pans the map; in that view it scrolls the page
    // (touch-action in sections/store-locator.liquid, read by the browser when a touch begins).
    this.viewport?.toggleAttribute("data-zoomed", s > this.#fit(this.#items).s * 1.05);
    const sx = (x) => (x - cx) * s + width / 2;
    const sy = (y) => (y - cy) * s + height / 2;

    // The drawing: the world outline zoomed out, the detailed one zoomed in, each loaded when
    // first needed; until then the other one stands in.
    const wantDetail = s >= DETAIL_FROM;
    this.#loadMap(wantDetail ? "detail" : "world");
    const detail = this.#maps.detail instanceof SVGElement ? this.#maps.detail : null;
    const world = this.#maps.world instanceof SVGElement ? this.#maps.world : null;
    const drawing = wantDetail ? detail || world : world || detail;
    const box = `${cx - width / 2 / s} ${cy - height / 2 / s} ${width / s} ${height / s}`;
    [world, detail].forEach((svg) => {
      if (!svg) return;
      svg.toggleAttribute("hidden", svg !== drawing);
      if (svg === drawing) svg.setAttribute("viewBox", box);
    });

    // Pins: visible locations, plus the picked one; close ones merge into a cluster.
    const selected = this.#find(this.#selected);
    const source = this.#items.filter((item) => item.visible);
    if (selected && !selected.visible) source.push(selected);
    let groups = [];
    source.forEach((item) => {
      const px = sx(item.x);
      const py = sy(item.y);
      const isSelected = item === selected;
      const group = isSelected ? null : groups.find((g) => !g.selected && Math.hypot(g.px - px, g.py - py) < CLUSTER_PX);
      if (group) group.members.push(item);
      else groups.push({ px, py, members: [item], selected: isSelected });
    });
    // Fully zoomed in, locations that still share a spot fan out around it instead.
    if (s >= ZOOM_MAX - 0.01) {
      groups = groups.flatMap((group) => {
        if (group.members.length < 2) return [group];
        const radius = CLUSTER_PX;
        return group.members.map((item, i) => {
          const angle = (2 * Math.PI * i) / group.members.length - Math.PI / 2;
          return {
            px: group.px + radius * Math.cos(angle),
            py: group.py + radius * Math.sin(angle),
            members: [item],
            selected: item === selected,
          };
        });
      });
    }

    while (this.#pins.length < groups.length) {
      const pin = document.createElement("button");
      pin.type = "button";
      pin.className = "map__pin";
      pin.tabIndex = -1;
      this.pinLayer.append(pin);
      this.#pins.push(pin);
    }
    this.#pins.forEach((pin, i) => {
      const group = groups[i];
      pin.hidden = !group;
      if (!group) return;
      const many = group.members.length > 1;
      const first = group.members[0];
      pin.style.transform = `translate(${group.px}px, ${group.py}px)`;
      pin.style.zIndex = group.selected ? 3 : many ? 2 : 1;
      if (many) {
        pin.dataset.count = String(group.members.length);
        pin.dataset.members = group.members.map((m) => m.id).join(",");
        delete pin.dataset.id;
        pin.setAttribute("aria-label", fill(this.strings.clusterLabel || "[count]", { count: group.members.length }));
        pin.removeAttribute("aria-current");
      } else {
        delete pin.dataset.count;
        delete pin.dataset.members;
        pin.dataset.id = first.id;
        pin.setAttribute("aria-label", `${first.name}, ${first.city}`);
        pin.setAttribute("aria-current", String(group.selected));
      }
    });

    // Labels: countries while zoomed out (large ones first, none on top of another), the cities
    // of the pins on the map once zoomed in.
    const labels = [];
    const taken = [];
    const free = (x, y, w, h) => {
      const box = [x - w / 2, y - h / 2, x + w / 2, y + h / 2];
      if (taken.some((b) => box[0] < b[2] && box[2] > b[0] && box[1] < b[3] && box[3] > b[1])) return false;
      taken.push(box);
      return true;
    };
    if (s < COUNTRIES_BELOW) {
      COUNTRIES.forEach(([code, x, y, area]) => {
        if (area * s * s < LABEL_AREA_PX) return;
        const px = sx(x);
        const py = sy(y);
        if (px < -80 || px > width + 80 || py < -30 || py > height + 30) return;
        const text = this.regions?.of(code) || code;
        if (!free(px, py, text.length * 9 + 8, 16)) return;
        labels.push({ key: `c-${code}`, kind: "country", text, x: px, y: py });
      });
    }
    if (s >= CITIES_FROM) {
      const cities = new Map();
      source.forEach((item) => {
        const key = norm(item.city);
        if (item.city && !cities.has(key)) cities.set(key, item);
      });
      cities.forEach((item, key) =>
        labels.push({ key: `t-${key}`, kind: "city", text: item.city, x: sx(item.x), y: sy(item.y) }));
    }
    if (this.#me) labels.push({ key: "me", kind: "me", text: "", x: sx(this.#me.x), y: sy(this.#me.y) });
    if (selected) {
      labels.push({ key: `tag-${selected.id}`, kind: "tag", text: selected.name, x: sx(selected.x), y: sy(selected.y) });
    }
    const inView = (l) => l.x > -80 && l.x < width + 80 && l.y > -30 && l.y < height + 30;
    const keep = new Set();
    labels.filter(inView).forEach((label) => {
      keep.add(label.key);
      let el = this.#labels.get(label.key);
      if (!el) {
        el = document.createElement("span");
        el.className = label.kind === "me" ? "map__me" : label.kind === "tag" ? "map__tag" : `map__label map__label--${label.kind}`;
        el.textContent = label.text;
        if (label.kind === "me") {
          el.setAttribute("role", "img");
          el.setAttribute("aria-label", this.strings.you || "");
          this.pinLayer.prepend(el);
        } else {
          (label.kind === "tag" ? this.pinLayer : this.labelLayer).append(el);
        }
        this.#labels.set(label.key, el);
      }
      el.style.transform = `translate(${label.x}px, ${label.y}px)`;
    });
    this.#labels.forEach((el, key) => {
      if (!keep.has(key)) {
        el.remove();
        this.#labels.delete(key);
      }
    });

    if (drawing) this.map?.setAttribute("data-ready", "");
  }

  /* Dragging and pinching. A drag starts after 4px, so a tap on a pin stays a click. */
  #onDown(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target.closest(".map__controls, .map__card")) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.#pointers.size === 1) {
      this.#drag = { x: event.clientX, y: event.clientY, view: { ...(this.#shown || this.#autoView()) }, moved: false };
    } else if (this.#pointers.size === 2) {
      this.#startPinch();
    }
  }

  // A pinch from the first two fingers that are down; a third one is ignored.
  #startPinch() {
    const [a, b] = [...this.#pointers.values()];
    this.#drag = {
      pinch: Math.hypot(a.x - b.x, a.y - b.y) || 1,
      mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      view: { ...(this.#shown || this.#autoView()) },
      moved: true,
    };
    this.#pointers.forEach((_, id) => {
      try {
        this.viewport.setPointerCapture?.(id);
      } catch {
        // The pointer may already be gone.
      }
    });
  }

  #onMove(event) {
    if (!this.#pointers.has(event.pointerId) || !this.#drag) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const start = this.#drag.view;
    if (this.#pointers.size >= 2 && this.#drag.pinch) {
      const [a, b] = [...this.#pointers.values()].slice(0, 2);
      const rect = this.viewport.getBoundingClientRect();
      const s = this.#clampZoom(start.s * (Math.hypot(a.x - b.x, a.y - b.y) / this.#drag.pinch));
      // Keep the map point under the fingers' midpoint where it was.
      const mx = this.#drag.mid.x - rect.left - rect.width / 2;
      const my = this.#drag.mid.y - rect.top - rect.height / 2;
      const px = start.cx + mx / start.s;
      const py = start.cy + my / start.s;
      const nx = (a.x + b.x) / 2 - rect.left - rect.width / 2;
      const ny = (a.y + b.y) / 2 - rect.top - rect.height / 2;
      this.#view = { s, cx: px - nx / s, cy: py - ny / s };
      this.#render();
      return;
    }
    const dx = event.clientX - this.#drag.x;
    const dy = event.clientY - this.#drag.y;
    if (!this.#drag.moved) {
      if (Math.hypot(dx, dy) < DRAG_PX) return;
      this.#drag.moved = true;
      this.viewport.setPointerCapture?.(event.pointerId);
      this.viewport.setAttribute("data-dragging", "");
    }
    this.#view = { s: start.s, cx: start.cx - dx / start.s, cy: start.cy - dy / start.s };
    this.#render();
  }

  #onUp(event) {
    this.#pointers.delete(event.pointerId);
    if (this.#pointers.size >= 2) {
      // Still two fingers down (a third had joined): pinch on from here.
      this.#startPinch();
      return;
    }
    if (this.#pointers.size === 1) {
      // One finger left after a pinch: carry on dragging from here.
      const [rest] = [...this.#pointers.values()];
      this.#drag = { x: rest.x, y: rest.y, view: { ...(this.#shown || this.#autoView()) }, moved: true };
      return;
    }
    if (this.#drag?.moved) {
      this.#suppressClick = true;
      setTimeout(() => {
        this.#suppressClick = false;
      }, 0);
    }
    this.#drag = null;
    this.viewport?.removeAttribute("data-dragging");
  }
}

if (!customElements.get("store-locator")) customElements.define("store-locator", StoreLocator);
